import { formatDanishLongDate } from "@/util/date";
import { DispensationEvidenceListModel } from "../models/dispensation";
import ClePagination from "./ClePagination";
import CleTable, { type CleTableColumn } from "./CleTable";
import ReportedStatusBadge from "./ReportedStatusBadge";
import { formatLessons } from "../utils/lessonLimits";

type DispensationsTableProps = {
  dispensations: DispensationEvidenceListModel[];
  isError?: boolean;
  isLoading?: boolean;
  onDispensationClick?: (dispensationId: string) => void;
  pagination?: {
    isDisabled?: boolean;
    onPageChange: (page: number) => void;
    page: number;
    total: number;
    totalPages: number;
  };
  showStatusColumn?: boolean;
};

const DispensationsTable = ({
  dispensations,
  isError = false,
  isLoading = false,
  onDispensationClick,
  pagination,
  showStatusColumn = true,
}: DispensationsTableProps) => {
  const columns: CleTableColumn<DispensationEvidenceListModel>[] = [
    {
      id: "fileName",
      header: "Dokument",
      title: (dispensation) => dispensation.fileName ?? "",
      cell: (dispensation) => dispensation.fileName ?? "-",
    },
    {
      id: "createdAt",
      header: "Oprettet",
      cell: (dispensation) => formatDanishLongDate(dispensation.createdAt),
    },
    {
      id: "lessons",
      header: "Lektioner",
      className: "text-end",
      cell: (dispensation) => formatLessons(dispensation.numberOfLessons),
    },
    ...(showStatusColumn
      ? [
          {
            id: "status",
            header: "Status",
            cell: (dispensation: DispensationEvidenceListModel) => (
              <ReportedStatusBadge reported={dispensation.isReported} />
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <CleTable
        columns={columns}
        data={dispensations}
        emptyDescription="Når der er tildelt dispensationer, vises de her i oversigten."
        emptyTitle="Ingen dispensationer fundet"
        errorMessage="Dispensationer kunne ikke indlæses lige nu."
        isError={isError}
        isLoading={isLoading}
        getRowId={(dispensation) => dispensation.id}
        rowAction={
          onDispensationClick
            ? {
                onClick: (id) => onDispensationClick(id),
              }
            : undefined
        }
      />
      {pagination != null && dispensations.length > 0 ? (
        <ClePagination
          ariaLabel="Dispensationer pagination"
          isDisabled={pagination.isDisabled}
          itemLabel="dispensationer"
          onPageChange={pagination.onPageChange}
          page={pagination.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
        />
      ) : null}
    </>
  );
};

export default DispensationsTable;
